import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import './Exam.css';

import useProctoring from '../hooks/useProctoring';
import ProctoringCamera from '../components/ProctoringCamera';

export default function Exam() {
  const { session_id } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  // ─── State ──────────────────────────────────────────────────────────────────
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [disqualified, setDisqualified] = useState(false);
  const [maxViolations, setMaxViolations] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [facePhotoSaved, setFacePhotoSaved] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [rulesConfirmed, setRulesConfirmed] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningDetail, setWarningDetail] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);

  // ─── Refs ───────────────────────────────────────────────────────────────────
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const answersRef = useRef({});
  const dirtyAnswersRef = useRef(new Set());
  const flushTimerRef = useRef(null);
  const flushPromiseRef = useRef(null);

  // ─── Device check ───────────────────────────────────────────────────────────
  const isMobileDevice = useMemo(() => {
    const ua = navigator.userAgent || '';
    const mobileUA = /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua);
    const coarsePointer = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches : false;
    const smallScreen = typeof window !== 'undefined' ? window.innerWidth <= 1024 : false;
    return mobileUA || (coarsePointer && smallScreen);
  }, []);

  const requireFullscreen = !isMobileDevice;

  const webcamVideoConstraints = useMemo(() => ({
    facingMode: { ideal: 'user' },
    width: { ideal: 640 },
    height: { ideal: 480 },
  }), []);

  // ─── Proctoring hook ────────────────────────────────────────────────────────
  // const {
  //   faceStatus,
  //   isFullscreen,
  //   enterFullscreen,
  //   violationCount,
  //   setViolationCount,
  //   registerViolation,
  // } = useProctoring({
  //   webcamRef,
  //   cameraError,
  //   active: examStarted,
  //   disqualified,
  //   setDisqualified,
  //   maxViolations,
  //   isMobileDevice,
  //   requireFullscreen,
  //   violationEndpoint: '/exam/violation',
  //   violationIdField: 'session_id',
  //   violationIdValue: session_id ? Number(session_id) : null,
  // });

  const handleViolation = useCallback(({ type, message, count, disqualified: isDisq }) => {
  if (isDisq) {
    setDisqualified(true);
    setShowWarningModal(false);
    return;
  }
  setWarningDetail(message);
  setShowWarningModal(true);
  setTimeout(() => setShowWarningModal(false), 5000);
}, []);

const {
  faceStatus,
  isFullscreen,
  enterFullscreen,
  violationCount,
  setViolationCount,
  registerViolation,
} = useProctoring({
  webcamRef,
  cameraError,
  active: examStarted,
  disqualified,
  setDisqualified,
  maxViolations,
  isMobileDevice,
  requireFullscreen,
  violationEndpoint: '/exam/violation',
  violationIdField: 'session_id',
  violationIdValue: session_id ? Number(session_id) : null,
  onViolation: handleViolation, // ← tambah ini
});

  // ─── Camera handlers ────────────────────────────────────────────────────────
  const handleCameraError = useCallback(() => {
    setCameraError(true);
    setError(
      isMobileDevice
        ? 'Kamera depan wajib digunakan. Pastikan izin kamera diberikan dan pilih kamera depan pada perangkat Anda.'
        : 'Kamera tidak tersedia. Pastikan izin kamera sudah diberikan.'
    );
  }, [isMobileDevice]);

  const handleUserMedia = useCallback((stream) => {
    if (!isMobileDevice) { setCameraError(false); return; }
    const track = stream?.getVideoTracks?.()[0];
    const settings = track?.getSettings ? track.getSettings() : {};
    const facingMode = String(settings?.facingMode || '').toLowerCase();
    const label = String(track?.label || '').toLowerCase();
    const looksBackCamera =
      facingMode === 'environment' ||
      label.includes('back') ||
      label.includes('rear') ||
      label.includes('environment');

    if (looksBackCamera) {
      stream.getTracks().forEach((t) => t.stop());
      setCameraError(true);
      setError('Kamera depan wajib digunakan. Silakan pilih front camera pada browser iPhone Anda.');
      return;
    }
    setCameraError(false);
    setError((prev) => (prev && prev.includes('Kamera depan wajib') ? '' : prev));
  }, [isMobileDevice]);

  // ─── Load exam ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get(`/exam/session/${session_id}`);
        const data = res.data;
        if (data.session.status === 'disqualified') {
          setDisqualified(true);
          setLoading(false);
          return;
        }
        setSession(data.session);
        setQuestions(data.questions);
        setRemainingSeconds(data.remainingSeconds);
        setExamStarted(!!data.session.exam_started);
        setMaxViolations(data.session.max_violations || 1);
        setViolationCount(data.session.violation_count || 0);
        const savedMap = {};
        data.questions.forEach(q => { if (q.savedAnswer) savedMap[q.id] = q.savedAnswer; });
        answersRef.current = savedMap;
        setAnswers(savedMap);
        const firstUnansweredIndex = data.questions.findIndex(
          (q) => savedMap[q.id] == null || savedMap[q.id] === ''
        );
        setCurrentIdx(
          firstUnansweredIndex >= 0
            ? firstUnansweredIndex
            : Math.max(0, data.questions.length - 1)
        );
      } catch (err) {
        setError(err.response?.data?.message || 'Gagal memuat ujian');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [session_id]); // eslint-disable-line

  // ─── Save face photo ────────────────────────────────────────────────────────
  const saveFacePhoto = useCallback(async () => {
    if (facePhotoSaved || !webcamRef.current) return;
    try {
      const screenshot = webcamRef.current.getScreenshot();
      if (screenshot) {
        await API.post('/exam/save-face', { session_id, screenshot });
        setFacePhotoSaved(true);
      }
    } catch (err) {
      console.error('Save face photo error:', err);
    }
  }, [facePhotoSaved, session_id]);

  // ─── Override registerViolation to show warning modal ───────────────────────
  // useProctoring sudah handle kirim ke backend, tapi exam perlu show warning modal
  // Wrap registerViolation dengan modal logic
  // const reportViolation = useCallback(async (type, description, screenshot = null) => {
  //   // Panggil registerViolation dari hook
  //   // Tapi kita perlu tahu hasilnya untuk show modal
  //   // Solusi: langsung call API seperti sebelumnya, tapi tetap sync state via hook
  //   try {
  //     const res = await API.post('/exam/violation', {
  //       session_id,
  //       violation_type: type,
  //       description,
  //       screenshot,
  //     });
  //     const newCount = res.data.violation_count;
  //     setViolationCount(newCount);
  //     if (res.data.disqualified) {
  //       setDisqualified(true);
  //       setShowWarningModal(false);
  //     } else {
  //       setWarningDetail(description);
  //       setShowWarningModal(true);
  //       setTimeout(() => setShowWarningModal(false), 5000);
  //     }
  //   } catch (e) {
  //     console.error('Violation log error', e);
  //   }
  // }, [session_id, setViolationCount]);

  // ─── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || !examStarted || remainingSeconds <= 0) return;
    timerRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [session, disqualified, examStarted]); // eslint-disable-line

  // ─── Batched auto-save answers ───────────────────────────────────────────────
  const flushAnswers = useCallback(async () => {
    if (!session_id) return;

    if (flushPromiseRef.current) {
      try { await flushPromiseRef.current; } catch (e) { /* sudah ditangani */ }
    }

    const pendingIds = Array.from(dirtyAnswersRef.current);
    if (pendingIds.length === 0) return;

    const payload = pendingIds
      .filter((qId) => answersRef.current[qId] != null)
      .map((qId) => ({ question_id: Number(qId), selected_option: answersRef.current[qId] }));

    dirtyAnswersRef.current.clear();
    if (payload.length === 0) return;

    const request = API.post('/exam/answers', { session_id, answers: payload })
      .catch(() => {
        // Kembalikan ke antrean agar dicoba lagi pada flush berikutnya.
        pendingIds.forEach((qId) => dirtyAnswersRef.current.add(qId));
      })
      .finally(() => {
        if (flushPromiseRef.current === request) flushPromiseRef.current = null;
      });

    flushPromiseRef.current = request;
    await request;
  }, [session_id]);

  const scheduleFlush = useCallback((delay = 1500) => {
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => { flushAnswers(); }, delay);
  }, [flushAnswers]);

  useEffect(() => {
    if (!session || disqualified || !examStarted) return undefined;

    autoSaveRef.current = setInterval(() => { flushAnswers(); }, 30000);

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flushAnswers();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(autoSaveRef.current);
      clearTimeout(flushTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [session, disqualified, examStarted, flushAnswers]);

  // ─── Face detection via canvas (skin pixel) ─────────────────────────────────
  useEffect(() => {
    if (!session || disqualified || cameraError) return;
    let faceMissingStart = null;

    const interval = setInterval(() => {
      if (!webcamRef.current || !canvasRef.current) return;
      const video = webcamRef.current.video;
      if (!video || video.readyState !== 4) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = 160;
      canvas.height = 120;
      ctx.drawImage(video, 0, 0, 160, 120);

      const imgData = ctx.getImageData(0, 0, 160, 120);
      const pixels = imgData.data;
      let skinPixels = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
        if (r > 80 && g > 40 && b > 20 && r > g && r > b && (r - g) > 15 && r < 250) {
          skinPixels++;
        }
      }
      const ratio = skinPixels / (160 * 120);
      const faceDetected = ratio > 0.05;

      if (faceDetected) {
        faceMissingStart = null;
        if (!facePhotoSaved) saveFacePhoto();
      } else {
        if (!faceMissingStart) {
          faceMissingStart = Date.now();
        } else if (examStarted && Date.now() - faceMissingStart >= 5000) {
          faceMissingStart = null;
          const screenshot = webcamRef.current?.getScreenshot?.() || null;
          registerViolation('face_missing', 'Wajah tidak terdeteksi selama lebih dari 5 detik', screenshot);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [session, disqualified, cameraError, examStarted, facePhotoSaved, saveFacePhoto, registerViolation]);

  // ─── Answer handler ──────────────────────────────────────────────────────────
  const selectAnswer = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    answersRef.current = { ...answersRef.current, [questionId]: option };
    dirtyAnswersRef.current.add(questionId);
    scheduleFlush();
  };

  // ─── Start exam ──────────────────────────────────────────────────────────────
  const startExamFlow = async () => {
    if (requireFullscreen && !isFullscreen) {
      setError('Aktifkan mode layar penuh terlebih dahulu.');
      return;
    }
    if (cameraError) {
      setError('Kamera tidak tersedia. Pastikan izin kamera sudah diberikan.');
      return;
    }
    if (faceStatus !== 'ok') {
      setError('Wajah belum terdeteksi dengan baik. Pastikan wajah terlihat jelas di kamera.');
      return;
    }
    if (!rulesConfirmed) {
      setError('Centang persetujuan peraturan sebelum mulai ujian.');
      return;
    }

    try {
      await saveFacePhoto();
      const beginRes = await API.post('/exam/begin', { session_id });
      if (typeof beginRes?.data?.remainingSeconds === 'number') {
        setRemainingSeconds(beginRes.data.remainingSeconds);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memulai ujian. Silakan coba lagi.');
      return;
    }

    setError('');
    setExamStarted(true);
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    clearInterval(autoSaveRef.current);
    clearTimeout(flushTimerRef.current);
    try {
      await flushAnswers();
      await API.post('/exam/submit', { session_id });
      navigate(`/result/${session_id}`);
    } catch (err) {
      setError('Gagal mengirim jawaban: ' + (err.response?.data?.message || ''));
      setSubmitting(false);
    }
  }, [submitting, session_id, navigate, flushAnswers]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // ─── Render: Disqualified ────────────────────────────────────────────────────
  if (disqualified) {
    return (
      <div className="disqualified-screen">
        <div className="disq-card">
          <div className="disq-icon">🚫</div>
          <h1>Anda Didiskualifikasi</h1>
          <p>Anda telah melakukan lebih dari {maxViolations} pelanggaran.</p>
          <p>Ujian Anda dinyatakan <strong>TIDAK VALID</strong>.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">Kembali ke Dashboard</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-screen"><div className="spinner"></div><p>Memuat ujian...</p></div>;
  if (error && !session) return <div className="error-screen"><p>{error}</p><button onClick={() => navigate('/dashboard')}>Kembali</button></div>;
  if (!session) return null;

  const currentQ = questions[currentIdx];
  const progress = ((currentIdx + 1) / questions.length) * 100;
  const answered = questions.filter(q => {
    const ans = answers[q.id];
    return typeof ans === 'string' && ans.trim().length > 0;
  }).length;
  const timerDanger = remainingSeconds < 300;
  const fullscreenReady = requireFullscreen ? isFullscreen : true;
  const canStartExam = fullscreenReady && !cameraError && faceStatus === 'ok' && rulesConfirmed;
  const currentExamIsMultipleChoice = session?.multiple_choice === 1;
  const isCurrentEssayAnswered = !!(answers[currentQ?.id] && String(answers[currentQ?.id]).trim());

  return (
    <div className="exam-page" style={{ userSelect: 'none' }}>

      {/* Warning modal */}
      {showWarningModal && (
        <div className="warning-overlay">
          <div className="warning-modal">
            <div className="warn-icon">⚠️</div>
            <h2>Peringatan Pelanggaran!</h2>
            <p>{warningDetail}</p>
            <p className="warn-count">Pelanggaran: <strong>{violationCount}</strong> / {maxViolations}</p>
            {violationCount >= maxViolations - 1 && violationCount < maxViolations && (
  <p className="warn-last">Pelanggaran berikutnya = DISKUALIFIKASI</p>
)}
          </div>
        </div>
      )}

      {/* Fullscreen prompt */}
      {requireFullscreen && !isFullscreen && (
        <div className="fullscreen-prompt">
          <div className="fs-card">
            <h2>⛶ Mode Layar Penuh Diperlukan</h2>
            <p>Ujian harus dijalankan dalam mode layar penuh</p>
            <button onClick={enterFullscreen} className="btn-primary">Aktifkan Layar Penuh</button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="exam-header">
        <div className="exam-title">{session.title}</div>
        <div className="exam-meta">
          {!requireFullscreen && <span className="violation-badge mobile-mode">📱 Mode Mobile</span>}
          <span className={`timer ${timerDanger ? 'danger' : ''}`}>⏱ {formatTime(remainingSeconds)}</span>
          <span className="progress-text">{answered}/{questions.length} dijawab</span>
          <span className={`violation-badge ${violationCount > 0 ? 'warn' : ''}`}>
            ⚠️ {violationCount}/{maxViolations}
          </span>
          <span className={`violation-badge ${faceStatus !== 'ok' ? 'warn' : ''}`}>
            {faceStatus === 'ok' ? '✅ Wajah Terdeteksi' : '⚠️ Wajah Tidak Terdeteksi'}
          </span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

{isMobileDevice && examStarted && (
  <div className="mobile-exam-tools">
    <div className="mobile-camera">
      <ProctoringCamera
        webcamRef={webcamRef}
        cameraError={cameraError}
        faceStatus={faceStatus}
        onUserMedia={handleUserMedia}
        onUserMediaError={handleCameraError}
        videoConstraints={webcamVideoConstraints}
        size="small"
      />
    </div>

    <button
      className="mobile-nav-toggle"
      onClick={() => setShowMobileNav((prev) => !prev)}
    >
      {showMobileNav ? '▲ Sembunyikan Soal' : '▼ Navigasi Soal'}
    </button>
  </div>
)}

{isMobileDevice && examStarted && showMobileNav && (
  <div className="mobile-question-grid">
    {questions.map((q, i) => (
      <button
        key={q.id}
        className={`q-btn ${i === currentIdx ? 'active' : ''} ${
          answers[q.id] ? 'answered' : ''
        }`}
        onClick={() => {
          setCurrentIdx(i);
          setShowMobileNav(false);
        }}
      >
        {i + 1}
      </button>
    ))}
  </div>
)}

      {!examStarted ? (
        <div className="precheck-screen">
          <div className="precheck-card">
            <h2>Persiapan Sebelum Mulai Ujian</h2>
            <p>Pastikan semua syarat terpenuhi sebelum mengerjakan soal.</p>

            <div className="precheck-grid">
              <div className="precheck-item">
                <span className={`check-dot ${fullscreenReady ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Mode Layar Penuh</strong>
                  <p>{requireFullscreen ? (isFullscreen ? 'Aktif' : 'Belum aktif') : 'Mode mobile (opsional)'}</p>
                </div>
                {requireFullscreen && !isFullscreen && (
                  <button className="btn-mini" onClick={enterFullscreen}>Aktifkan</button>
                )}
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${!cameraError ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Kamera</strong>
                  <p>{cameraError ? 'Kamera belum tersedia' : 'Kamera aktif'}</p>
                </div>
              </div>

              <div className="precheck-item">
                <span className={`check-dot ${faceStatus === 'ok' ? 'ok' : 'wait'}`}></span>
                <div>
                  <strong>Deteksi Wajah</strong>
                  <p>{faceStatus === 'ok' ? 'Wajah terdeteksi' : 'Wajah belum terdeteksi'}</p>
                </div>
              </div>
            </div>

            <div className="precheck-camera">
              <ProctoringCamera
                webcamRef={webcamRef}
                cameraError={cameraError}
                faceStatus={faceStatus}
                onUserMedia={handleUserMedia}
                onUserMediaError={handleCameraError}
                videoConstraints={webcamVideoConstraints}
                size="large"
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>

            <div className="rules-box precheck-rules">
              <h3>⚠️ Peraturan Ujian</h3>
              <ul>
                {requireFullscreen ? (
                  <li>Ujian wajib dilakukan dalam mode layar penuh</li>
                ) : (
                  <li>Untuk perangkat mobile, mode layar penuh bersifat opsional</li>
                )}
                <li>Dilarang berpindah tab atau keluar jendela browser</li>
                <li>Kamera wajah harus aktif selama ujian</li>
                <li>Wajah harus terdeteksi selama ujian berlangsung</li>
                <li>Copy-paste dinonaktifkan selama ujian</li>
                <li>Lebih dari <strong>{maxViolations} pelanggaran</strong> akan didiskualifikasi</li>
              </ul>
            </div>

            <label className={`confirm-rule ${rulesConfirmed ? 'confirm-rule--checked' : 'confirm-rule--unchecked'}`}>
              <input type="checkbox" checked={rulesConfirmed} onChange={(e) => setRulesConfirmed(e.target.checked)} />
              Saya sudah membaca dan menyetujui seluruh peraturan ujian.
            </label>

            {error && <div className="psy-error">{error}</div>}

            <button className="btn-submit start-btn" onClick={startExamFlow} disabled={!canStartExam}>
              Mulai Kerjakan Soal
            </button>
          </div>
        </div>
      ) : (
        <div className="exam-body">
          <aside className="q-nav">
  <h3>Navigasi Soal</h3>

  {!isMobileDevice && (
    <>
      <div className="q-grid">
        {questions.map((q, i) => (
          <button
            key={q.id}
            className={`q-btn ${i === currentIdx ? 'active' : ''} ${
              answers[q.id] ? 'answered' : ''
            }`}
            onClick={() => setCurrentIdx(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="legend">
        <span className="leg answered"></span> Dijawab
        <span className="leg active"></span> Aktif
        <span className="leg empty"></span> Belum
      </div>

      <div className="camera-box">
        <ProctoringCamera
          webcamRef={webcamRef}
          cameraError={cameraError}
          faceStatus={faceStatus}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleCameraError}
          videoConstraints={webcamVideoConstraints}
          size="small"
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </>
  )}
</aside>

          <main className="q-main">
            {currentQ && (
              <div className="question-card">
                <div className="q-number">Soal {currentIdx + 1} dari {questions.length}</div>
                <p className="q-text">{currentQ.question_text}</p>
                {currentQ.photo && (
                  <div className="question-photo">
                    <img
                      src={currentQ.photo.startsWith('http') ? currentQ.photo : `/uploads/questions/${currentQ.photo}`}
                      alt={`Soal ${currentIdx + 1}`}
                    />
                  </div>
                )}
                <div className="options">
                  {currentExamIsMultipleChoice ? (
                    ['a', 'b', 'c', 'd', 'e'].map(opt => {
                      const optText = currentQ[`option_${opt}`];
                      if (!optText) return null;
                      return (
                        <button
                          key={opt}
                          className={`option-btn ${answers[currentQ.id] === opt ? 'selected' : ''}`}
                          onClick={() => selectAnswer(currentQ.id, opt)}
                        >
                          <span className="opt-label">{opt.toUpperCase()}</span>
                          <span className="opt-text">{optText}</span>
                        </button>
                      );
                    })
                  ) : (
                    <div className="essay-answer-box">
                      <label>Jawaban Essay (wajib diisi)</label>
                      <textarea
                        className="essay-textarea"
                        value={answers[currentQ.id] || ''}
                        onChange={(e) => selectAnswer(currentQ.id, e.target.value)}
                        placeholder="Tulis jawaban Anda di sini..."
                        rows={8}
                      />
                    </div>
                  )}
                </div>

                <div className="q-nav-btns">
                  <button
                    className="btn-nav"
                    onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                    disabled={currentIdx === 0}
                  >
                    ← Sebelumnya
                  </button>
                  {currentIdx < questions.length - 1 ? (
                    <button
                      className="btn-nav btn-next"
                      onClick={() => setCurrentIdx(i => i + 1)}
                      disabled={!currentExamIsMultipleChoice && !isCurrentEssayAnswered}
                    >
                      Berikutnya →
                    </button>
                  ) : (
                    <button
                      className="btn-submit"
                      onClick={handleSubmit}
                      disabled={submitting || (!currentExamIsMultipleChoice && !isCurrentEssayAnswered)}
                    >
                      {submitting ? 'Mengirim...' : '✅ Kumpulkan Ujian'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}